import React from 'react';
import _ from 'lodash';
import history from 'app/routes/history';
import HeaderThin from 'app/components/layout/HeaderThin';
import {
  ListGroup,
  ListGroupItem,
  ListGroupItemHeading,
  ListGroupItemText,
  Badge,
} from 'reactstrap';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import SectionGroup from 'app/components/general/SectionGroup';
import Button from 'app/components/general/Button';
import Loader from 'app/components/general/Loader';
import { simpleDate } from 'app/utils/parsers';

import * as fixtureMatchImportsActions from 'app/ducks/fixtureMatchImports/actions';
import * as fixtureMatchImportsSelectors from 'app/ducks/fixtureMatchImports/selectors';

interface ICsvRunDetailProps {
  appReady: boolean;
  actions: any;
  match: any;
  errors: any;
  fixtureMatchImports: any;
  connectionRuns: any;
  connections: any;
}

interface ICsvRunDetailState {
  counter: number;
}

function selector(state): any {
  return {
    fixtureMatchImports: fixtureMatchImportsSelectors.getFixtureMatchImports(
      state,
    ),
  };
}

const actionCreators = _.merge(fixtureMatchImportsActions);

@connect(selector, (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
}))
export default class CsvRunDetail extends React.Component<
  ICsvRunDetailProps,
  ICsvRunDetailState
> {
  apiIntervals: any;

  static defaultProps: ICsvRunDetailProps = {
    appReady: false,
    actions: undefined,
    match: undefined,
    errors: undefined,
    fixtureMatchImports: undefined,
    connections: undefined,
    connectionRuns: undefined,
  };

  state = {
    counter: 0,
  };

  componentDidMount = (): void => {
    const connectionRun = this.props.connectionRuns.currentConnectionRun;
    if (
      !_.isEmpty(connectionRun)
      && !_.isEmpty(connectionRun.metadata)
      && !_.isEmpty(connectionRun.metadata.paymentsDataImportId)
    ) {
      this.props.actions.getFixtureMatchImport(
        connectionRun.metadata.paymentsDataImportId,
      );
    }
    this.apiIntervals = setInterval(() => this.pollFixtureMatchImport(), 10000);
  };

  componentWillUnmount = (): void => {
    clearInterval(this.apiIntervals);
  };

  beginProcessing = (): void => {
    if (
      this.props.appReady
      && !this.props.fixtureMatchImports.isLoading
      && this.props.fixtureMatchImports.hasLoaded
      && !this.props.fixtureMatchImports.updatedFixtureMatchImport
      && this.props.fixtureMatchImports.currentFixtureMatchImport.status
        === 'pending'
    ) {
      const payload = {
        id: this.props.match.params.importId,
        status: 'processing',
      };
      this.props.actions.updateFixtureMatchImport(payload);
    }
  };

  pollFixtureMatchImport = (): boolean => {
    // console.log('polling');
    if (
      this.state.counter < 30
      && !this.props.fixtureMatchImports.isLoading
      && this.props.fixtureMatchImports.currentFixtureMatchImport.status
        !== 'finished'
    ) {
      this.setState((previousState) => ({
        counter: previousState.counter + 1,
      }));
      // console.log('polling api');
      this.beginProcessing();
      const connectionRun = this.props.connectionRuns.currentConnectionRun;
      if (
        !_.isEmpty(connectionRun)
        && !_.isEmpty(connectionRun.metadata)
        && !_.isEmpty(connectionRun.metadata.paymentsDataImportId)
      ) {
        this.props.actions.getFixtureMatchImport(
          connectionRun.metadata.paymentsDataImportId,
        );
      }
    }
    return true;
  };

  renderFullResults = (fullResults): React.ReactNode => {
    if (fullResults === undefined) {
      return null;
    }
    return _.map(fullResults, (value, key) => {
      let color = '';
      if (value > 0 && _.includes(key, 'error')) {
        color = 'danger';
      } else if (value > 0 && _.includes(key, 'success')) {
        color = 'secondary';
      } else {
        color = 'info';
      }
      return (
        <ListGroupItemText key={key}>
          {_.startCase(key)}
          {' '}
          <Badge color={color} pill>
            {value}
          </Badge>
        </ListGroupItemText>
      );
    });
  };

  render(): React.ReactNode {
    return (
      <div>
        <div className="row">
          <div className="col">
            <HeaderThin
              breadcrumb="Connections"
              breadcrumbLink="/connections"
              title="Import Status"
            />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <SectionGroup>
              <div className="row">
                <div className="col">
                  <ListGroup>
                    <ListGroupItem className="justify-content-between">
                      <ListGroupItemHeading>Import Type</ListGroupItemHeading>
                      <ListGroupItemText>
                        {this.props.connectionRuns.currentConnectionRun.name}
                      </ListGroupItemText>
                    </ListGroupItem>
                    <ListGroupItem className="justify-content-between">
                      <ListGroupItemHeading>
                        Connection Type
                      </ListGroupItemHeading>
                      <ListGroupItemText>
                        {_.upperCase(
                          this.props.connectionRuns.currentConnectionRun.code,
                        )}
                      </ListGroupItemText>
                    </ListGroupItem>
                    <ListGroupItem className="justify-content-between">
                      <ListGroupItemHeading>Status</ListGroupItemHeading>
                      <div className="with-margin-bottom">
                        {this.props.fixtureMatchImports
                          .currentFixtureMatchImport.status !== 'processing'
                          && _.capitalize(
                            this.props.fixtureMatchImports
                              .currentFixtureMatchImport.status,
                          )}
                        <div className="d-inline-block inline-loader">
                          <Loader
                            color="grey"
                            isActive={
                              this.props.fixtureMatchImports
                                .currentFixtureMatchImport.status !== 'finished'
                            }
                          />
                        </div>
                      </div>
                    </ListGroupItem>
                    <ListGroupItem className="justify-content-between">
                      <ListGroupItemHeading>Date Imported</ListGroupItemHeading>
                      <ListGroupItemText>
                        {simpleDate(
                          this.props.fixtureMatchImports
                            .currentFixtureMatchImport.createdAt,
                        )}
                      </ListGroupItemText>
                    </ListGroupItem>
                    <ListGroupItem className="justify-content-between">
                      <ListGroupItemHeading>Results</ListGroupItemHeading>
                      <ListGroupItemText>
                        Successful
                        {' '}
                        <Badge pill>
                          {
                            this.props.fixtureMatchImports
                              .currentFixtureMatchImport.successCount
                          }
                        </Badge>
                      </ListGroupItemText>
                      <ListGroupItemText>
                        Failed
                        {' '}
                        <Badge pill>
                          {
                            this.props.fixtureMatchImports
                              .currentFixtureMatchImport.failCount
                          }
                        </Badge>
                      </ListGroupItemText>
                      <ListGroupItemText>
                        Total
                        {' '}
                        <Badge pill>
                          {
                            this.props.fixtureMatchImports
                              .currentFixtureMatchImport.totalCount
                          }
                        </Badge>
                      </ListGroupItemText>
                    </ListGroupItem>
                    <ListGroupItem className="justify-content-between">
                      <ListGroupItemHeading>Full Results</ListGroupItemHeading>
                      {this.renderFullResults(
                        this.props.fixtureMatchImports.currentFixtureMatchImport
                          .importSummary,
                      )}
                    </ListGroupItem>
                    <ListGroupItem className="justify-content-between">
                      <ListGroupItemHeading>
                        Import Identifier
                      </ListGroupItemHeading>
                      <ListGroupItemText>
                        {
                          this.props.fixtureMatchImports
                            .currentFixtureMatchImport.id
                        }
                      </ListGroupItemText>
                    </ListGroupItem>
                  </ListGroup>
                </div>
              </div>
              <div className="row with-margin-top">
                <div className="col btn-flex-group less-padding">
                  <div className="btn-flex-container">
                    <Button
                      className="btn-standard btn-1"
                      type="submit"
                      onClick={() => history.push('/connections')}
                    >
                      Finished
                    </Button>
                  </div>
                </div>
              </div>
            </SectionGroup>
          </div>
        </div>
      </div>
    );
  }
}
